import React, { useState, useEffect } from "react";
import MobileWrapper from "../../components/MobileWrapper";
import {
  addNewDoc,
  getAllDocsSnap,
  storage,
} from "../../utils/firebase/Firebase.utils";
import { useNavigate } from "react-router-dom";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiCalendar,
  FiClock,
  FiUpload,
  FiX,
  FiPlus,
  FiCheck,
  FiHome,
  FiSearch,
  FiMapPin,
  FiFileText,
} from "react-icons/fi";
import { v4 } from "uuid";
import { FaIdCard, FaUsers } from "react-icons/fa";
import { unitNumberLabel } from "../../utils/constants";

export default function GuestForm() {
  const currentUser = useSelector((state) => state.userObject);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [residents, setResidents] = useState([]);
  const [unitSearchTerm, setUnitSearchTerm] = useState("");
  const [filteredUnits, setFilteredUnits] = useState([]);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [requestId, setRequestId] = useState(v4().slice(0, 8).toUpperCase());

  // Color scheme
  const eleghoBlue = "#363062";
  const eleghoGreen = "#8FCD00";

  // Form state
  const [formData, setFormData] = useState({
    requestorName: "",
    date: "",
    time: "",
    linkedIdentifiers: [],
    purpose: "",
    status: "pending",
    requestDate: new Date().toISOString(),
    requestedBy: "",
    guests: [
      {
        name: "",
        plateNumber: "",
        email: "",
        identification: "",
        isUploading: false,
      },
    ],
    history: [
      {
        by: currentUser?.email || "Guest",
        at: new Date().toLocaleString(),
        action: "Created",
        to: "",
      },
    ],
    updatedAt: new Date().toLocaleString(),
  });

  // Fetch residents data from Firestore
  useEffect(() => {
    const fetchResidents = async () => {
      await getAllDocsSnap("residents", (snap) => {
        setResidents(snap);
      });
    };
    fetchResidents();
  }, []);

  // Filter units based on search term
  useEffect(() => {
    if (unitSearchTerm.trim() === "") {
      setFilteredUnits([]);
      return;
    }

    const term = unitSearchTerm.toLowerCase().trim();
    const filtered = residents.filter((resident) => {
      // Check if resident has identifier property
      if (!resident.identifier) return false;

      // If identifier is a string, search in it
      if (typeof resident.identifier === "string") {
        return resident.identifier.toLowerCase().includes(term);
      }

      // If identifier is an array, search in each element
      if (Array.isArray(resident.identifier)) {
        return resident.identifier.some((id) =>
          id.toLowerCase().includes(term)
        );
      }

      return false;
    });

    setFilteredUnits(filtered);
  }, [unitSearchTerm, residents]);

  // Handle basic form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle unit search input
  const handleUnitSearch = (e) => {
    setUnitSearchTerm(e.target.value);
    setShowUnitDropdown(true);
  };

  // Handle unit selection from dropdown
  const handleUnitSelect = (identifier) => {
    if (identifier && !formData.linkedIdentifiers.includes(identifier)) {
      setFormData({
        ...formData,
        linkedIdentifiers: [...formData.linkedIdentifiers, identifier],
      });
    }
    setUnitSearchTerm("");
    setShowUnitDropdown(false);
  };

  // Remove a unit from the linkedIdentifiers array
  const handleRemoveUnit = (unit) => {
    setFormData({
      ...formData,
      linkedIdentifiers: formData.linkedIdentifiers.filter((u) => u !== unit),
    });
  };

  // Handle guest information changes
  const handleGuestChange = (index, field, value) => {
    const updatedGuests = [...formData.guests];
    updatedGuests[index] = {
      ...updatedGuests[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      guests: updatedGuests,
    });
  };

  // Function to handle ID image upload
  const handleIdImageUpload = async (index, file) => {
    if (!file) return;

    try {
      // Show loading state for this specific guest
      const updatedGuests = [...formData.guests];
      updatedGuests[index] = {
        ...updatedGuests[index],
        isUploading: true,
      };
      setFormData({
        ...formData,
        guests: updatedGuests,
      });

      // Create a unique filename using timestamp and uuid
      const filename = `guests/${file.name}_${v4()}`;

      // Create a reference to the file location in Firebase Storage
      const storageRef = ref(storage, filename);

      // Upload file
      await uploadBytes(storageRef, file);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      // Update the guest with the identification URL
      const updatedGuestsWithUrl = [...formData.guests];
      updatedGuestsWithUrl[index] = {
        ...updatedGuestsWithUrl[index],
        identification: downloadURL,
        isUploading: false,
      };

      setFormData({
        ...formData,
        guests: updatedGuestsWithUrl,
      });
    } catch (error) {
      console.error("Error uploading ID image:", error);
      alert("Error uploading ID image. Please try again.");

      // Clear loading state
      const updatedGuests = [...formData.guests];
      updatedGuests[index] = {
        ...updatedGuests[index],
        isUploading: false,
      };
      setFormData({
        ...formData,
        guests: updatedGuests,
      });
    }
  };

  // Add a new guest
  const handleAddGuest = () => {
    setFormData({
      ...formData,
      guests: [
        ...formData.guests,
        {
          name: "",
          plateNumber: "",
          email: "",
          identification: "",
          isUploading: false,
        },
      ],
    });
  };

  // Remove a guest field
  const handleRemoveGuest = (index) => {
    if (formData.guests.length > 1) {
      const updatedGuests = formData.guests.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        guests: updatedGuests,
      });
    }
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Validate form data
      if (
        !formData.requestorName ||
        !formData.date ||
        !formData.time ||
        formData.linkedIdentifiers.length === 0
      ) {
        alert("Please fill in all required fields");
        setIsLoading(false);
        return;
      }

      // Check if all guests have names
      const validGuests = formData.guests.filter(
        (guest) => guest.name.trim() !== ""
      );
      if (validGuests.length === 0) {
        alert("Please add at least one guest");
        setIsLoading(false);
        return;
      }

      // Only include guests with names
      const submitData = {
        ...formData,
        guests: validGuests,
        requestDate: new Date().toISOString(),
        updatedAt: new Date().toLocaleString(),
        history: [
          ...formData.history,
          {
            by: "System",
            at: new Date().toLocaleString(),
            action: "Submitted",
            to: "",
          },
        ],
      };

      // Submit to Firebase
      await addNewDoc("guests", submitData);
      alert("Guest registration request submitted successfully");
      navigate("/guests");
    } catch (error) {
      console.error("Error submitting guest request:", error);
      alert("Error submitting request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Close dropdown when clicking outside
  const handleClickOutside = () => {
    setShowUnitDropdown(false);
  };

  return (
    <MobileWrapper label="Guest Registration">
      <div className="min-h-screen bg-gray-50 p-4 pb-6">
        {/* Header similar to Gatepass.jsx */}
        <div className="flex items-center justify-between mb-5 bg-[#363062]/5 p-3 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaUsers className="text-white text-sm" />
            </div>
            <div>
              <h1 className="text-xs font-medium text-[#363062]">
                Guest Registration
              </h1>
              <p className="text-xs text-gray-500">
                Register visitors for security clearance
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Ref: {requestId}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Requestor Information */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-lg shadow-sm p-4"
          >
            <h2
              className="text-xs font-semibold mb-3 flex items-center gap-2"
              style={{ color: eleghoBlue }}
            >
              <FiUser className="w-3.5 h-3.5" />
              Requestor Information
            </h2>

            <div className="space-y-3">
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                  <FiUser className="w-3.5 h-3.5 text-gray-500" />
                  Your Name*
                </label>
                <input
                  type="text"
                  name="requestorName"
                  value={formData.requestorName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                  <FiMail className="w-3.5 h-3.5 text-gray-500" />
                  Email Address*
                </label>
                <input
                  type="email"
                  name="requestedBy"
                  value={formData.requestedBy}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                  placeholder="Enter your email address"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                    <FiCalendar className="w-3.5 h-3.5 text-gray-500" />
                    Date*
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                    required
                  />
                </div>
                <div>
                  <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                    <FiClock className="w-3.5 h-3.5 text-gray-500" />
                    Time*
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                    required
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Unit Selection */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-lg shadow-sm p-4"
          >
            <h2
              className="text-xs font-semibold mb-3 flex items-center gap-2"
              style={{ color: eleghoBlue }}
            >
              <FiHome className="w-3.5 h-3.5" />
              Unit Information
            </h2>

            <div className="space-y-3">
              <div className="relative">
                <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                  <FiSearch className="w-3.5 h-3.5 text-gray-500" />
                  Search Unit*
                </label>
                <input
                  type="text"
                  value={unitSearchTerm}
                  onChange={handleUnitSearch}
                  onFocus={() => setShowUnitDropdown(true)}
                  onBlur={() => setTimeout(handleClickOutside, 200)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                  placeholder={`Search by ${unitNumberLabel} or name`}
                />

                {/* Dropdown for unit search results */}
                {showUnitDropdown && filteredUnits.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 max-h-48 overflow-auto bg-white border border-gray-200 rounded-md shadow-lg">
                    {filteredUnits.map((resident, index) => {
                      // Handle different identifier formats
                      const identifier =
                        typeof resident.identifier === "string"
                          ? resident.identifier
                          : Array.isArray(resident.identifier)
                          ? resident.identifier[0]
                          : "";

                      if (!identifier) return null;

                      return (
                        <div
                          key={index}
                          className="p-2 text-xs hover:bg-gray-50 cursor-pointer flex items-center gap-2"
                          onClick={() => handleUnitSelect(identifier)}
                        >
                          <FiHome className="w-3.5 h-3.5 text-eleghoBlue" />
                          {identifier}
                          {resident.name && ` - ${resident.name}`}
                        </div>
                      );
                    })}
                  </div>
                )}

                {showUnitDropdown &&
                  unitSearchTerm &&
                  filteredUnits.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 p-2 bg-white border border-gray-200 rounded-md shadow-lg text-xs">
                      No units found matching "{unitSearchTerm}"
                    </div>
                  )}
              </div>

              {/* Display selected units */}
              {formData.linkedIdentifiers.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-1.5 block">
                    Selected Units:
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {formData.linkedIdentifiers.map((unit) => (
                      <div
                        key={unit}
                        className="px-2 py-1 rounded-md text-white text-xs flex items-center gap-1.5"
                        style={{ backgroundColor: eleghoBlue }}
                      >
                        <FiHome className="w-3 h-3" />
                        {unit}
                        <button
                          type="button"
                          onClick={() => handleRemoveUnit(unit)}
                          className="ml-1 w-3.5 h-3.5 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                        >
                          <FiX className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Purpose of Visit */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm p-4"
          >
            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5">
              <FiFileText className="w-3.5 h-3.5 text-eleghoBlue" />
              Purpose of Visit
            </label>
            <textarea
              name="purpose"
              value={formData.purpose}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
              rows="3"
              placeholder="Please state the purpose of the visit"
            />
          </motion.div>

          {/* Guest Information */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-3"
          >
            <h2
              className="text-xs font-semibold flex items-center gap-2"
              style={{ color: eleghoBlue }}
            >
              <FaIdCard className="w-3.5 h-3.5" />
              Guest Information
            </h2>

            {formData.guests.map((guest, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="bg-white rounded-lg shadow-sm p-4 relative"
              >
                <div className="flex justify-between items-center mb-2">
                  <div
                    className="text-xs font-semibold"
                    style={{ color: eleghoBlue }}
                  >
                    Visitor #{index + 1}
                  </div>
                  {formData.guests.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveGuest(index)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded-full hover:bg-gray-100"
                    >
                      <FiX className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="space-y-2.5">
                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                      <FiUser className="w-3.5 h-3.5 text-gray-500" />
                      Guest Name*
                    </label>
                    <input
                      type="text"
                      value={guest.name}
                      onChange={(e) =>
                        handleGuestChange(index, "name", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                      placeholder="Enter guest's full name"
                      required
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                      <FiMail className="w-3.5 h-3.5 text-gray-500" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={guest.email || ""}
                      onChange={(e) =>
                        handleGuestChange(index, "email", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                      placeholder="Enter guest's email address"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                      <FiMapPin className="w-3.5 h-3.5 text-gray-500" />
                      Vehicle Plate Number
                    </label>
                    <input
                      type="text"
                      value={guest.plateNumber || ""}
                      onChange={(e) =>
                        handleGuestChange(index, "plateNumber", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-eleghoBlue/20 focus:border-eleghoBlue text-xs"
                      placeholder="Enter vehicle plate number (if applicable)"
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1">
                      <FiUpload className="w-3.5 h-3.5 text-gray-500" />
                      ID Upload
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                          handleIdImageUpload(index, e.target.files[0])
                        }
                        className="hidden"
                        id={`idUpload-${index}`}
                        disabled={guest.isUploading}
                      />
                      <label
                        htmlFor={`idUpload-${index}`}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-md cursor-pointer hover:bg-gray-50 transition-colors text-center text-xs text-gray-600 flex items-center justify-center gap-2"
                      >
                        <FiUpload className="w-3.5 h-3.5" />
                        {guest.isUploading
                          ? "Uploading..."
                          : guest.identification
                          ? "Change ID Photo"
                          : "Choose file"}
                      </label>
                    </div>
                    {guest.isUploading && (
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-1">
                        <div
                          className="bg-eleghoBlue h-1 rounded-full animate-pulse"
                          style={{ width: "60%" }}
                        ></div>
                      </div>
                    )}
                    {guest.identification && !guest.isUploading && (
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex items-center text-xs text-green-600 gap-1">
                          <FiCheck className="w-3 h-3" />
                          ID uploaded
                        </div>
                        <img
                          src={guest.identification}
                          alt="ID Preview"
                          className="h-8 rounded border border-gray-200 ml-auto"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}

            <motion.button
              type="button"
              onClick={handleAddGuest}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-2 border border-dashed rounded-md text-xs flex items-center justify-center gap-2 transition-colors"
              style={{ borderColor: eleghoGreen, color: eleghoGreen }}
            >
              <FiPlus className="w-3.5 h-3.5" />
              Add Another Guest
            </motion.button>
          </motion.div>

          {/* Submit Button */}
          <div className="space-y-3 pt-2">
            <motion.button
              type="submit"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-2.5 rounded-md text-white text-xs font-medium flex items-center justify-center gap-2"
              style={{ backgroundColor: eleghoBlue }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <FiCheck className="w-3.5 h-3.5" />
                  Submit Guest Request
                </>
              )}
            </motion.button>

            <motion.button
              type="button"
              onClick={() => navigate("/guests")}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-2.5 border rounded-md text-xs"
              style={{ borderColor: "#ddd", color: "#666" }}
              disabled={isLoading}
            >
              Cancel
            </motion.button>
          </div>

          <p className="text-[10px] text-center text-gray-500 pt-1">
            Notice: Upon submission, your request will be reviewed by the
            management. Guests may be asked to present valid IDs upon arrival.
          </p>
        </form>
      </div>
    </MobileWrapper>
  );
}
